import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import Yup from 'yup';
import { getActiveSpace } from 'spaces/index';
import { closeModal } from 'modal/store/actions';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import Title3 from 'base/components/Title3';
import MainTitle from 'base/components/MainTitle';
import SubmitButton from 'base/components/forms/SubmitButton';
import FormikTextareaField from 'base/components/forms/formik/FormikTextareaField';
import ModalHeader from 'modal/components/ModalHeader';
import { getActiveFolder, getActivePathStringWithSpace } from '../../store/folders/selectors';
import { createFolder, createMultipleFolders } from '../../store/folders/actions';

const CreateFolderForm = ({errors, isSubmitting}) => ( // eslint-disable-line
  <Form>
    <Field
      type="text"
      name="name"
      placeholder="Name"
      component={FormikTextareaField}
      autoFocus
      rows={6}
    />
    <FieldWrapper>
      You can add multiple folders at once, one folder per line.
      {' '}
      <br />
      Child folders can be prefixed with a dash &apos;-&apos; (one dash per hierarchy level).
      <br />
      <small style={{ lineHeight: '1.2rem', display: 'inline-block' }}>
      Example:
        {' '}
        <br />
      Test 1
        {' '}
        <br />
      -Test 1.1
        {' '}
        <br />
      --Test 1.1.1
        {' '}
        <br />
      -Test 1.2
        <br />
      Test2
        <br />
      </small>
    </FieldWrapper>
    <SubmitButton type="submit" disabled={false}>
        Create Folders
    </SubmitButton>
  </Form>
);

/**
 * Parses a flat text based list of folders into a hierarchical structure
 * One folder per line. Child folders must be prefixed with a dash '-', one dash per hierarchy level
 * @param names
 * @returns {*}
 */
const parseFolderTree = (names) => names.reduce(
  (folders, prefixedName) => {
    const insertFolder = (folders, newFolder) => {
      const currentNode = folders[folders.length - 1];
      if (currentNode.depth === newFolder.depth - 1) {
        currentNode.children.push(newFolder);
      } else {
        currentNode.children = insertFolder(currentNode.children, newFolder);
      }
      return folders;
    };

    const name = prefixedName.replace(/^[-]+|[-]+$/, '');
    const depth = parseInt(prefixedName.length, 10) - parseInt(name.length, 10);
    const newFolder = { name, depth, children: [] };
    if (depth === 0) {
      return [...folders, newFolder];
    }
    insertFolder(folders, newFolder);
    return folders;
  },
  [],
);

const CreateFolderModal = ({
  name,
  folder,
  space,
  createFolder,
  createMultipleFolders,
  closeModal,
  activePathString,
}) => (
  <>
    <ModalHeader>
      <MainTitle>New Folder</MainTitle>
    </ModalHeader>

    <FieldWrapper>
        Folder will be created in
      <Title3>{activePathString}</Title3>
    </FieldWrapper>

    <Formik
      initialValues={{
        name: '',
      }}
      validationSchema={
            Yup.object().shape({
              name: Yup.string()
                .required('Name is required.'),
            })
          }
      render={CreateFolderForm}
      onSubmit={(values) => {
        const names = values.name.split('\n');
        if (names.length > 1) {
          const multipleFolders = {
            space: space.id,
            folders: parseFolderTree(names),
          };
          if (folder) {
            multipleFolders.parent = folder.id;
          }
          createMultipleFolders(multipleFolders);
        } else {
          const newFolder = {
            name: names[0],
            space: space.id,
          };
          if (folder) {
            newFolder.parent = folder.id;
          }
          createFolder(newFolder);
        }
        closeModal(name);
      }}
    />
  </>
);
CreateFolderModal.propTypes = {
  name: PropTypes.string.isRequired,
  space: PropTypes.objectOf(PropTypes.any).isRequired,
  folder: PropTypes.objectOf(PropTypes.any).isRequired,
  createFolder: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  activePathString: PropTypes.string.isRequired,
  createMultipleFolders: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  space: getActiveSpace(state),
  folder: getActiveFolder(state),
  activePathString: getActivePathStringWithSpace(state),
});

const mapDispatchToProps = {
  createFolder,
  createMultipleFolders,
  closeModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateFolderModal);
